import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "semantic-ui-react";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { Store } from "../../api";
import { useCart } from "../../hooks";
import { STORE_INFO } from "../../utils";
import styles from "./WebLayout.module.scss";

const MOBILE_TABS = [
  { label: "Inicio", path: "/", icon: "home", end: true },
  { label: "Catálogo", path: "/catalogo", icon: "grid layout" },
  { label: "Carrito", path: "/carrito", icon: "shopping bag" },
];

const SOCIAL_ICON_PATHS = {
  whatsapp:
    "M12.04 2.5c-5.26 0-9.54 4.28-9.54 9.54 0 1.68.44 3.31 1.28 4.75L2.5 21.5l4.86-1.27a9.5 9.5 0 0 0 4.68 1.23h.01c5.26 0 9.54-4.28 9.54-9.54 0-2.55-.99-4.94-2.79-6.74a9.48 9.48 0 0 0-6.76-2.68zm0 17.46h-.01a7.9 7.9 0 0 1-4.03-1.1l-.29-.17-3 .78.8-2.93-.19-.3a7.94 7.94 0 0 1-1.22-4.2c0-4.38 3.56-7.94 7.95-7.94a7.9 7.9 0 0 1 5.62 2.33 7.9 7.9 0 0 1 2.32 5.62c0 4.39-3.57 7.95-7.95 7.95zm4.36-5.95c-.24-.12-1.42-.7-1.64-.78-.22-.08-.38-.12-.54.12-.16.24-.62.78-.76.94-.14.16-.28.18-.52.06-.24-.12-1-.37-1.9-1.17-.7-.63-1.18-1.4-1.31-1.64-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.31-.74-1.79-.19-.47-.39-.4-.54-.41h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.7 2.59 4.11 3.63.57.25 1.02.4 1.37.51.58.18 1.1.16 1.51.1.46-.07 1.42-.58 1.62-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28z",
  instagram:
    "M12 2c2.72 0 3.06.01 4.12.06 1.06.05 1.79.22 2.43.47.66.26 1.21.6 1.76 1.15.55.55.9 1.1 1.15 1.76.25.64.42 1.37.47 2.43.05 1.06.06 1.4.06 4.12s-.01 3.06-.06 4.12c-.05 1.06-.22 1.79-.47 2.43a4.9 4.9 0 0 1-1.15 1.76 4.9 4.9 0 0 1-1.76 1.15c-.64.25-1.37.42-2.43.47-1.06.05-1.4.06-4.12.06s-3.06-.01-4.12-.06c-1.06-.05-1.79-.22-2.43-.47a4.9 4.9 0 0 1-1.76-1.15 4.9 4.9 0 0 1-1.15-1.76c-.25-.64-.42-1.37-.47-2.43C2.01 15.06 2 14.72 2 12s.01-3.06.06-4.12c.05-1.06.22-1.79.47-2.43.26-.66.6-1.21 1.15-1.76A4.9 4.9 0 0 1 5.44 2.53c.64-.25 1.37-.42 2.43-.47C8.94 2.01 9.28 2 12 2zm0 3.5A6.5 6.5 0 1 0 12 18.5 6.5 6.5 0 0 0 12 5.5zm0 10.72A4.22 4.22 0 1 1 12 7.78a4.22 4.22 0 0 1 0 8.44zM17.9 5.4a1.52 1.52 0 1 1-3.04 0 1.52 1.52 0 0 1 3.04 0z",
};

export function WebLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { itemCount } = useCart();

  const [categories, setCategories] = useState([]);
  const [searchValue, setSearchValue] = useState(
    searchParams.get("search") || ""
  );

  // Modo mantenimiento real: mientras esté activo (Admin >
  // Configuración), NINGUNA página pública se muestra -- ni Inicio, ni
  // catálogo, ni producto, ni carrito -- solo el mensaje. No afecta al
  // panel de administración (usa otro layout) ni a /login, así que el
  // equipo puede seguir entrando a desactivarlo. Mientras
  // isCheckingMaintenance es true todavía no sabemos si está activo, así
  // que no se muestra ni la tienda ni la pantalla de mantenimiento (para
  // no destellar el catálogo un instante antes de bloquearlo).
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true);

  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const lastScrollYRef = useRef(0);

  const navRef = useRef(null);
  const [showNavScrollHint, setShowNavScrollHint] = useState(false);

  const socialLinks = [
    STORE_INFO.WHATSAPP_NUMBER
      ? {
          key: "whatsapp",
          label: "WhatsApp",
          href: `https://wa.me/${STORE_INFO.WHATSAPP_NUMBER}`,
        }
      : null,
    STORE_INFO.SOCIAL.instagram
      ? {
          key: "instagram",
          label: "Instagram",
          href: STORE_INFO.SOCIAL.instagram,
        }
      : null,
  ].filter(Boolean);

  const loadCategories = useCallback(async () => {
    try {
      const storeController = new Store();
      const result = await storeController.getCategories();

      setCategories(result?.categories || []);
    } catch (error) {
      console.error("WebLayout loadCategories error:", error);

      setCategories([]);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    async function loadMaintenanceBanner() {
      try {
        const storeController = new Store();
        const result = await storeController.getSettings();
        const settings = result?.settings || result?.response || result || {};

        const isActive = Boolean(settings?.maintenanceBanner?.active);
        const message = settings?.maintenanceBanner?.message || "";

        setMaintenanceMessage(isActive && message ? message : "");
      } catch (error) {
        console.error("WebLayout loadMaintenanceBanner error:", error);

        // Si no se pudo confirmar el estado (ej. el servidor no
        // respondió), se deja pasar a la tienda normal en vez de
        // bloquear a todo el mundo por un error de red.
        setMaintenanceMessage("");
      } finally {
        setIsCheckingMaintenance(false);
      }
    }

    loadMaintenanceBanner();
  }, []);

  useEffect(() => {
    setSearchValue(searchParams.get("search") || "");
  }, [searchParams]);

  useEffect(() => {
    function handleScroll() {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollYRef.current;

      if (currentScrollY <= 150) {
        setIsHeaderHidden(false);
      } else if (delta > 6) {
        setIsHeaderHidden(true);
      } else if (delta < -6) {
        setIsHeaderHidden(false);
      }

      lastScrollYRef.current = currentScrollY;
    }

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const nav = navRef.current;

    if (!nav) {
      return;
    }

    function updateScrollHint() {
      const canScrollMore = nav.scrollWidth - nav.clientWidth - nav.scrollLeft > 4;

      setShowNavScrollHint(canScrollMore);
    }

    updateScrollHint();

    nav.addEventListener("scroll", updateScrollHint, { passive: true });
    window.addEventListener("resize", updateScrollHint);

    return () => {
      nav.removeEventListener("scroll", updateScrollHint);
      window.removeEventListener("resize", updateScrollHint);
    };
  }, [categories]);

  function handleSearchSubmit(event) {
    event.preventDefault();

    const trimmedValue = searchValue.trim();

    navigate(
      trimmedValue
        ? `/catalogo?search=${encodeURIComponent(trimmedValue)}`
        : "/catalogo"
    );
  }

  if (isCheckingMaintenance) {
    return null;
  }

  if (maintenanceMessage) {
    return (
      <div className={styles.MaintenancePage}>
        <div className={styles.MaintenanceCard}>
          <img
            src="/images/logo-header.jpg"
            alt="Lux & Glam"
            className={styles.MaintenanceLogo}
          />

          <p className={styles.MaintenanceMessage} role="status">
            {maintenanceMessage}
          </p>

          {STORE_INFO.WHATSAPP_NUMBER && (
            <a
              href={`https://wa.me/${STORE_INFO.WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.MaintenanceWhatsApp}
            >
              Escríbenos por WhatsApp
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.WebLayout}>
      <header
        className={`${styles.Header} ${
          isHeaderHidden ? styles.HeaderHidden : ""
        }`}
      >
        <div className={styles.HeaderTop}>
          <Link to="/" className={styles.LogoMark}>
            <img
              src="/images/logo-header.jpg"
              alt="Lux & Glam"
              className={styles.LogoMarkImage}
            />
          </Link>

          <Link to="/" className={styles.Brand}>
            LUX &amp; GLAM
          </Link>

          <form
            className={styles.SearchForm}
            onSubmit={handleSearchSubmit}
          >
            <Icon name="search" />

            <input
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Buscar"
              aria-label="Buscar productos"
            />
          </form>

          <div className={styles.HeaderActions}>
            {socialLinks.length > 0 && (
              <div className={styles.SocialLinks}>
                {socialLinks.map((social) => (
                  <a
                    key={social.key}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.SocialLink}
                    aria-label={social.label}
                  >
                    <svg viewBox="0 0 24 24" width="26" height="26">
                      <path
                        fill="currentColor"
                        d={SOCIAL_ICON_PATHS[social.key]}
                      />
                    </svg>
                  </a>
                ))}
              </div>
            )}

            <Link to="/carrito" className={styles.CartLink}>
              <Icon name="shopping bag" />

              {itemCount > 0 && (
                <span className={styles.CartBadge}>{itemCount}</span>
              )}
            </Link>
          </div>
        </div>

        {categories.length > 0 && (
          <div className={styles.HeaderNavWrapper}>
            <nav className={styles.HeaderNav} ref={navRef}>
              {categories.map((category) => (
                <NavLink
                  key={category._id}
                  to={`/catalogo/${category.slug}`}
                  className={({ isActive }) =>
                    `${styles.NavLink} ${isActive ? styles.NavLinkActive : ""}`
                  }
                >
                  {category.name}
                </NavLink>
              ))}
            </nav>

            {showNavScrollHint && (
              <span className={styles.HeaderNavScrollHint} aria-hidden="true">
                ›
              </span>
            )}
          </div>
        )}
      </header>

      <main className={styles.Content}>{children}</main>

      <footer className={styles.Footer}>
        <span className={styles.FooterBrand}>LUX &amp; GLAM</span>

        <p className={styles.FooterCopyright}>
          © {new Date().getFullYear()} Lux &amp; Glam. Todos los derechos
          reservados.
        </p>
      </footer>

      <nav className={styles.MobileTabBar}>
        {MOBILE_TABS.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            end={tab.end}
            className={({ isActive }) =>
              `${styles.MobileTab} ${isActive ? styles.MobileTabActive : ""}`
            }
          >
            <span className={styles.MobileTabIconWrapper}>
              <Icon name={tab.icon} />

              {tab.path === "/carrito" && itemCount > 0 && (
                <span className={styles.MobileTabBadge}>{itemCount}</span>
              )}
            </span>

            <span>{tab.label}</span>
          </NavLink>
        ))}
      </nav>

      {STORE_INFO.WHATSAPP_NUMBER && (
        <a
          href={`https://wa.me/${STORE_INFO.WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.WhatsAppFloat}
          aria-label="Escríbenos por WhatsApp"
        >
          <svg viewBox="0 0 24 24" width="30" height="30">
            <path fill="currentColor" d={SOCIAL_ICON_PATHS.whatsapp} />
          </svg>
        </a>
      )}
    </div>
  );
}
